"use client";

import { useMemo, useState } from "react";
import AlphabetFilter from "@/components/lexikon/AlphabetFilter";
import CategoryFilter from "@/components/lexikon/CategoryFilter";
import LexikonList from "@/components/lexikon/LexikonList";
import SearchBar from "@/components/lexikon/SearchBar";
import {
  firstLetter,
  lexikonActiveLetters,
  lexikonSorted,
  LEXIKON_CATEGORIES,
  type LexikonTerm,
} from "@/lib/lexikon";

type Category = LexikonTerm["category"];

/**
 * Container der Lexikon-Übersicht: hält Such-, Buchstaben- und Kategoriefilter,
 * berechnet die gefilterte Liste und rendert Suche, Filter und Ergebnisliste.
 */
export default function LexikonOverview() {
  const [search, setSearch] = useState("");
  const [letter, setLetter] = useState<string | null>(null);
  const [category, setCategory] = useState<Category | null>(null);

  const available = useMemo(() => lexikonActiveLetters(), []);

  const counts = useMemo(() => {
    const base = Object.fromEntries(
      LEXIKON_CATEGORIES.map((c) => [c, 0]),
    ) as Record<Category, number>;
    for (const term of lexikonSorted) base[term.category] += 1;
    return base;
  }, []);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return lexikonSorted.filter((term) => {
      if (category && term.category !== category) return false;
      if (letter && firstLetter(term.term) !== letter) return false;
      if (!query) return true;
      return (
        term.term.toLowerCase().includes(query) ||
        term.short.toLowerCase().includes(query) ||
        term.category.toLowerCase().includes(query)
      );
    });
  }, [search, letter, category]);

  function reset() {
    setSearch("");
    setLetter(null);
    setCategory(null);
  }

  return (
    <section className="bg-paper">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {/* Filterleiste */}
        <div className="flex flex-col items-center gap-8">
          <SearchBar value={search} onChange={setSearch} resultCount={filtered.length} />
          <CategoryFilter active={category} counts={counts} onSelect={setCategory} />
          <AlphabetFilter active={letter} available={available} onSelect={setLetter} />
        </div>

        {/* Ergebnisliste */}
        <div className="mt-14">
          <LexikonList terms={filtered} onReset={reset} />
        </div>
      </div>
    </section>
  );
}
